import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { map, Observable, switchMap, tap } from 'rxjs';
import mqtt, { type MqttClient } from 'mqtt';
import { CurrencyConfigService } from '../../core/currency-config.service';
import { I18nService } from '../../core/i18n.service';
import { MediaStorageService } from '../../core/media-storage.service';
import { formatAdPriceLabel, getCategoryLabel, getDealTypeLabel } from '../../core/realty-labels';
import { AuthService } from '../auth/auth.service';
import {
  AuthenticationRequestIntent,
  authenticationRequiredContext,
} from '../../core/authentication-required.context';

export interface ChatListMessage {
  id: number;
  user_id: number;
  username: string;
  avatar: string;
  timestamp: number;
  text: string;
  media: string[] | null;
  status: number;
}

export interface ChatListItem {
  id: number;
  realty_id: number;
  avatar: string;
  title: string;
  status: number;
  unread_amount: number;
  last_message: ChatListMessage | null;
}

export interface ChatUser {
  user_id: number;
  name: string;
  avatar: string;
  is_me: boolean;
}

export interface ChatThread {
  id: number;
  title: string;
  realty_id: number;
  users: ChatUser[];
  avatar: string;
  status: number;
}

export interface ChatMessage {
  id: number;
  user_id: number;
  username: string;
  avatar: string;
  timestamp: number;
  text: string;
  media: string[] | null;
  status: number;
}

export interface ChatRealtimeMessage {
  message_id: number;
  user_id: number;
  chat_id: number;
  timestamp: number;
  text: string | null;
  media: string[] | null;
  status: number;
}

interface ChatRealtimeTopic {
  topic: string;
  permission: string;
}

interface ChatRealtimeConfig {
  options: {
    password: string;
    username: string;
  };
  topics: string[];
  url: string;
}

interface ChatsResponse {
  chats: ChatListItem[] | null;
  code: number;
  message: string;
}

interface ChatResponse {
  chat: ChatThread;
  code: number;
  message: string;
}

interface ChatMessagesResponse {
  chat_messages: ChatMessage[] | null;
  code: number;
  message: string;
}

interface CreateChatResponse {
  chat_id: number;
  code: number;
  message: string;
}

interface SendMessageResponse {
  code: number;
  message: string;
}

interface AdAuthor {
  id: number;
}

interface AdMedia {
  filename: string;
}

interface AdResponse {
  ad: {
    id: number;
    type: number;
    deal_type: number;
    rent_type: number;
    price: number | null;
    currency?: string | null;
    original_price?: number | null;
    original_currency?: string | null;
    display_price?: number | null;
    display_currency?: string | null;
    media: AdMedia[] | null;
    author: AdAuthor;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly currencyConfig = inject(CurrencyConfigService);
  private readonly i18n = inject(I18nService);
  private readonly mediaStorage = inject(MediaStorageService);
  readonly unreadCount = signal(0);

  listChats(intent: AuthenticationRequestIntent = 'interactive'): Observable<ChatsResponse> {
    return this.http
      .get<ChatsResponse>('/chat-api/chats?offset=0&limit=20', {
        headers: this.buildChatHeaders(),
        context: authenticationRequiredContext(intent),
      })
      .pipe(tap((response) => this.setUnreadCountFromChats(response.chats ?? [])));
  }

  refreshUnreadCount(): Observable<number> {
    return this.listChats('background').pipe(map(() => this.unreadCount()));
  }

  setUnreadCountFromChats(chats: ChatListItem[]): void {
    this.unreadCount.set(
      chats.reduce((total, chat) => total + Math.max(0, chat.unread_amount ?? 0), 0)
    );
  }

  clearUnreadCount(): void {
    this.unreadCount.set(0);
  }

  getChat(chatId: number): Observable<ChatResponse> {
    return this.http.get<ChatResponse>(`/chat-api/chat/${chatId}`, {
      headers: this.buildChatHeaders(),
      context: authenticationRequiredContext(),
    });
  }

  getMessages(chatId: number): Observable<ChatMessagesResponse> {
    return this.http.get<ChatMessagesResponse>(`/chat-api/chat/${chatId}/messages?offset=0&limit=20`, {
      headers: this.buildChatHeaders(),
      context: authenticationRequiredContext(),
    });
  }

  getAdPreview(realtyId: number): Observable<AdResponse> {
    return this.http.get<AdResponse>(`/api/ad/${realtyId}`, {
      params: { display_currency: this.currencyConfig.getDisplayCurrencyParam() },
    });
  }

  getRealtimeConfig(): ChatRealtimeConfig | null {
    const mqttToken = this.authService.getMqttToken();

    if (!mqttToken) {
      return null;
    }

    const tokenData = mqttToken.split('.');
    if (tokenData.length < 2) {
      return null;
    }

    try {
      const payload = JSON.parse(this.decodeBase64Url(tokenData[1])) as {
        acl?: ChatRealtimeTopic[];
      };
      const topics = (payload.acl ?? [])
        .filter((topic) => topic.permission === 'allow')
        .map((topic) => topic.topic)
        .filter(Boolean);

      return {
        options: {
          password: mqttToken,
          username: 'User',
        },
        topics,
        url: 'wss://mqtt.realty.one:8783/mqtt',
      };
    } catch {
      return null;
    }
  }

  connectRealtime(
    onMessage: (topic: string, payload: ChatRealtimeMessage | null) => void
  ): MqttClient | null {
    const config = this.getRealtimeConfig();
    if (!config || !config.topics.length) {
      return null;
    }

    const client = mqtt.connect(config.url, {
      password: config.options.password,
      reconnectPeriod: 1000,
      username: config.options.username,
    });

    client.on('connect', () => {
      config.topics.forEach((topic) => {
        client.subscribe(topic, { qos: 0 });
      });
    });

    client.on('message', (topic, message) => {
      try {
        const payload = JSON.parse(
          new TextDecoder().decode(message)
        ) as ChatRealtimeMessage;
        onMessage(topic, payload);
      } catch {
        onMessage(topic, null);
      }
    });

    return client;
  }

  createChatFromAd(userId: number, realtyId: number): Observable<CreateChatResponse> {
    return this.http.get<AdResponse>(`/api/ad/${realtyId}`, {
      params: { display_currency: this.currencyConfig.getDisplayCurrencyParam() },
    }).pipe(
      switchMap((response) =>
        this.http.post<CreateChatResponse>(
          '/chat-api/chat',
          {
            user_id: userId,
            realty_id: realtyId,
            avatar: response.ad.media?.[0]?.filename ?? '',
            title: `${getCategoryLabel(this.i18n.locale(), response.ad.type)}, ${getDealTypeLabel(this.i18n.locale(), response.ad.deal_type)}`,
          },
          {
            headers: this.buildChatHeaders(),
            context: authenticationRequiredContext(),
          }
        )
      )
    );
  }

  sendMessage(chatId: number, text: string): Observable<SendMessageResponse> {
    return this.http.post<SendMessageResponse>(
      `/chat-api/chat/${chatId}/message`,
      { text },
      {
        headers: this.buildChatHeaders(),
        context: authenticationRequiredContext(),
      }
    );
  }

  markViewed(chatId: number): Observable<SendMessageResponse> {
    return this.http.put<SendMessageResponse>(
      `/chat-api/chat/${chatId}/viewed`,
      {},
      {
        headers: this.buildChatHeaders(),
        context: authenticationRequiredContext(),
      }
    );
  }

  buildStorageImage(path: string, size = '600:400'): string {
    return this.mediaStorage.buildImageUrl(path, size);
  }

  private buildChatHeaders(): HttpHeaders {
    const token = this.authService.getChatToken();

    return new HttpHeaders(token ? { Authorization: `Bearer ${token}` } : {});
  }

  formatPriceLabel(ad: AdResponse['ad']): string {
    return formatAdPriceLabel(this.i18n.locale(), ad);
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    const padded = padding ? normalized.padEnd(normalized.length + (4 - padding), '=') : normalized;

    if (typeof atob === 'function') {
      return atob(padded);
    }

    throw new Error('Base64 decoding is unavailable');
  }
}
